package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.AnimationAst
import top.wcpe.allincore.dsl.compiler.ast.ClassDecl
import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ImportDecl
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StateAst
import top.wcpe.allincore.dsl.compiler.ast.TooltipContentAst
import top.wcpe.allincore.dsl.compiler.ast.dataClassDecls
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * 组件展开阶段：把 screen root 中引用的自定义组件递归展开为纯内置元素树。
 *
 * 后序遍历 ElementAst 树，遇到 kind 首字母大写的节点按组件调用流程展开：
 * 查找注册表 → 校验 props → clone root → props 替换 → children 替换 → id 前缀化。
 *
 * 内部委托：
 * - [PropValidator]：buildPropMap / validatePropsRefs 等校验逻辑
 * - [ExpressionSubstitutor]：PropsRef 替换（无状态）
 * - [PropsRefLeakDetector]：DSL_E311 防御围栏
 */
class ComponentExpanderPass(
    private val registry: ComponentRegistry,
    private val diag: DiagnosticCollector,
    /**
     * IDE 单文件分析路径下，[CompileOptions.knownComponentNames] 透传过来的"项目级
     * 已知 component 名"白名单。命中此集合的调用即使在 [registry] 中找不到也不报
     * E300——它们是跨文件 component 引用，由 [ScreenWithComponentsPipeline] 在多文件
     * 编译时统一展开。
     *
     * 默认空集合，不影响 IDE 之外的常规编译流程。
     */
    private val knownComponentNames: Set<String> = emptySet(),
) {

    companion object {
        /** 递归展开最大深度，防止无限递归。 */
        private const val MAX_DEPTH = 32

        /**
         * UIElement 通用属性白名单：调用方在自定义组件根上写这些属性时，
         * 不强制要求组件声明对应 prop，而是直接挂到组件展开后的根元素上。
         * 用于 visible / opacity / pointerEvents / overflow / aboveItem / animateLayout 等运行时元素级控制，
         * 以及 grid 子项放置（gridColumn / gridRow）与宽高比约束（aspectRatio）等元素级布局属性。
         *
         * 须与 [IdeMetadata.universalElementProps] 及 PropMetadata 的 universalProps 保持「三处一致」。
         */
        private val UNIVERSAL_ELEMENT_PROPS = setOf(
            "visible",
            "opacity",
            "pointerEvents",
            "overflow",
            "aboveItem",
            "animateLayout",
            "gridColumn",
            "gridRow",
            "aspectRatio",
        )
    }

    private val propValidator = PropValidator(diag)
    private val leakDetector = PropsRefLeakDetector(diag)

    /**
     * M6 review B：当前 screen 的 data 声明索引。
     *
     * 仅在 [expand] 入口写入一次，子方法只读访问。用于校验调用方传给 data 类型 prop
     * 的 Map 字面量字段（必填 + 类型）。
     */
    private var dataDecls: Map<String, ClassDecl> = emptyMap()

    /**
     * 命名空间化后的组件缓存：原始 [ComponentAst.id] → 经过
     * [ComponentAnimationNamespacer.apply] 改写的副本。
     *
     * 同一个组件可能被多次实例化，缓存避免重复构造。
     */
    private val prefixedComponentCache = LinkedHashMap<String, ComponentAst>()

    /**
     * 当前 screen 实际展开过的组件 id 集合（按需合并 animation 用）。
     *
     * 仅在 [expandComponentCall] 命中真实组件时写入，外层包装组件 / 兜底分支不计入。
     */
    private val expandedComponentIds = LinkedHashSet<String>()

    /**
     * 本次 [expand] 收集到的「提升后组件 state 条目」：每个组件实例的 state 字段经
     * `instanceId/field` 改名后并入此处，收尾时统一并入 [ScreenAst.state]。
     *
     * 用 LinkedHashMap 以 mangled 名去重并保留首次出现顺序（实例 id 全局唯一，理论上不会撞名，
     * 去重仅作防御）。条目值是改名后的 [StateAst.StateEntry]（name 已是 mangled 名）。
     */
    private val hoistedStateEntries = LinkedHashMap<String, StateAst.StateEntry>()

    /**
     * 当前正在展开的元素所属文件的 import 别名表（alias → 注册路径，ADR-0038）。
     *
     * 解析采用「就近文件」语义：screen root 用 screen 的 import；展开进某组件 root 时切换为
     * 该组件的 import（[expandComponentCall] 在递归前后 save/restore）；注入的插槽内容已在调用方
     * 上下文展开过、不受影响。深度优先单线程，故用字段而非逐调用参数透传。
     */
    private var currentImports: Map<String, String> = emptyMap()

    /** 入口：展开 screen root 中的所有组件引用。 */
    fun expand(screen: ScreenAst): ScreenAst {
        // M6 review B：缓存 data 声明，供 data 类型 prop 校验复用
        dataDecls = screen.dataClassDecls().associateBy { it.name }
        prefixedComponentCache.clear()
        expandedComponentIds.clear()
        hoistedStateEntries.clear()
        // ADR-0038：校验 screen 与所有组件的 import（别名重复 E328 / 目标不存在 E327）。
        validateAllImports(screen)
        currentImports = toAliasMap(screen.imports)
        val expandedRoot = expandElement(screen.root, 0)
        validateOverlayComponentRefs(expandedRoot)
        // DSL_E311：展开完成后扫一遍产物，确保没有任何未替换的 PropsRef 漏出来。
        // 这道防御围栏避免 codegen 阶段因发现 PropsRef 抛 IllegalStateException，
        // 把未来 substitutePropsInExpr 漏处理某种 sealed 子类型的问题以清晰诊断暴露。
        leakDetector.assertNoLeakedPropsRef(expandedRoot)
        // 仅把"被实际引用到的组件"的（命名空间化后的）animation 合并到 screen.animations。
        // 同名时 screen 已有定义优先，组件之间不会再冲突（id 已带组件名前缀）。
        val mergedAnimations = mergeComponentAnimations(screen)
        // 计划 04 里程碑 5：把各实例提升 + 改名后的组件 state 条目并入 screen.state，
        // 之后 SemanticChecker / StateEmitter / runtime buildStates 自然为每实例字段建独立 Signal（运行时 0 改动）。
        val mergedState = mergeComponentStates(screen)
        return screen.copy(root = expandedRoot, animations = mergedAnimations, state = mergedState)
    }

    /**
     * 本次 [expand] 实际展开用到的组件名集合（已含传递依赖：A 组件 root 内引用 B 组件时，
     * 递归展开会一并记录 B）。供服务端建「组件 → 引用它的 screen」反向索引做精细化热重载。
     * 须在 [expand] 之后调用。
     */
    fun usedComponentIds(): Set<String> = LinkedHashSet(expandedComponentIds)

    /** import 声明列表 → 别名表（alias → path）；重复别名以最后为准（E328 已单独诊断）。 */
    private fun toAliasMap(imports: List<ImportDecl>): Map<String, String> =
        imports.associate { it.alias to it.path }

    /** 校验 screen 与所有已注册组件的 import 声明（ADR-0038）。 */
    private fun validateAllImports(screen: ScreenAst) {
        validateImports(screen.imports)
        for (comp in registry.all().values) validateImports(comp.imports)
    }

    /** 单个文件的 import 校验：别名重复 E328、目标路径不存在 E327。 */
    private fun validateImports(imports: List<ImportDecl>) {
        if (imports.isEmpty()) return
        val seen = mutableSetOf<String>()
        for (imp in imports) {
            if (!seen.add(imp.alias)) {
                diag.error(DslError.E328_DUPLICATE_IMPORT_ALIAS, imp.position, imp.alias)
            }
            if (registry.find(imp.path) == null) {
                diag.error(DslError.E327_IMPORT_TARGET_NOT_FOUND, imp.position, imp.path)
            }
        }
    }

    private fun validateOverlayComponentRefs(element: ElementAst) {
        listOfNotNull(element.rightClickMenuContent, element.contextMenuContent).forEach { content ->
            if (!registry.resolvable(content.name)) {
                diag.error(DslError.E735_OVERLAY_COMPONENT_UNREGISTERED, content.position, content.name)
            }
        }
        val toastComponent = element.toastContent as? top.wcpe.allincore.dsl.compiler.ast.TooltipContentAst.Component
        if (toastComponent != null && !registry.resolvable(toastComponent.name)) {
            diag.error(DslError.E742_TOAST_COMPONENT_INVALID, toastComponent.position, toastComponent.name)
        }
        element.children.forEach(::validateOverlayComponentRefs)
        // 风险 4 遍历审计：未展开成功的组件调用（如 E300/E303 提前返回）仍残留具名插槽内容，
        // 其中的 overlay 组件引用同样需要校验。
        element.namedSlotContent.values.forEach { content -> content.forEach(::validateOverlayComponentRefs) }
        element.menuBlocks.forEach { menu -> validateOverlayComponentRefs(menu.root) }
        // RFC-RECOMPOSITION：conditional 分支体内的元素也需要校验
        element.conditionalContent?.let { content ->
            content.branches.forEach { validateOverlayComponentRefs(it.body) }
            content.elseBranch?.let(::validateOverlayComponentRefs)
        }
        // $for：循环体内的元素也需要校验
        element.forContent?.let { validateOverlayComponentRefs(it.body) }
    }

    /** 把已被实际引用到的组件的 animation 合并进 screen.animations，screen 已有同 id 优先保留。 */
    private fun mergeComponentAnimations(screen: ScreenAst): List<AnimationAst> {
        val seen = LinkedHashSet<String>()
        val result = mutableListOf<AnimationAst>()
        screen.animations.forEach { a ->
            if (seen.add(a.id)) result += a
        }
        // 仅处理本次展开实际命中的组件（按命中顺序），未使用的组件的 animation 不进 IR
        expandedComponentIds.forEach { compId ->
            val comp = prefixedComponentCache[compId] ?: return@forEach
            comp.animations.forEach { a ->
                if (seen.add(a.id)) result += a
            }
        }
        return result
    }

    /**
     * 计划 04 里程碑 5：把组件实例的 state 字段提升 + 改名。
     *
     * - 为组件声明的每个 state 字段生成 mangled 名 `instanceId/field`，
     *   并把改名后的默认值条目收集进 [hoistedStateEntries]（收尾并入 screen.state）。
     * - 把组件模板 [template] 内所有对这些字段的 `state.field` 读写改名为 `instanceId/field`。
     *
     * [instanceId] 必须与 [prefixIds] 用同一来源（call.id），保证嵌套实例的 state 与元素 id 同源叠加前缀。
     * 组件无 state 声明 / instanceId 为空时原样返回（无 state 组件零行为变化）。
     */
    private fun hoistComponentState(
        template: ElementAst,
        comp: ComponentAst,
        instanceId: String,
    ): ElementAst {
        val state = comp.state ?: return template
        if (state.entries.isEmpty() || instanceId.isEmpty()) return template
        val fieldNames = state.entries.mapTo(LinkedHashSet()) { it.name }
        // 收集改名后的默认值条目：name 改为 mangled 名，literal / type / position 保持不变。
        for (entry in state.entries) {
            val mangled = "$instanceId/${entry.name}"
            hoistedStateEntries[mangled] = entry.copy(name = mangled)
        }
        // 把模板内对这些字段的 state.field 读写统一改名为 instanceId/field。
        return ExpressionSubstitutor.substituteStateNames(template, instanceId, fieldNames)
    }

    /**
     * 把各实例提升 + 改名后的组件 state 条目并入 screen.state。
     *
     * 与 [mergeComponentAnimations] 同款套路：screen 已有 state 字段优先保留在前，
     * 再追加各实例 mangled state 条目（mangled 名含实例 id，与 screen 级裸字段名天然不冲突）。
     * 无任何提升条目且 screen 本身也无 state 时返回 null，保持无 state 组件 + 无 state screen 的旧行为。
     */
    private fun mergeComponentStates(screen: ScreenAst): StateAst? {
        if (hoistedStateEntries.isEmpty()) return screen.state
        val merged = mutableListOf<StateAst.StateEntry>()
        screen.state?.entries?.let { merged.addAll(it) }
        merged.addAll(hoistedStateEntries.values)
        // position 取 screen.state 的位置（无则取第一个提升条目的位置），仅用于诊断定位。
        val pos = screen.state?.position ?: hoistedStateEntries.values.first().position
        return StateAst(entries = merged, position = pos)
    }

    /** 递归展开单个元素；非组件调用时仅递归 children。 */
    private fun expandElement(element: ElementAst, depth: Int): ElementAst {
        // 先递归展开 children 与菜单块（后序）。
        val expandedChildren = element.children.map { expandElement(it, depth) }
        // 风险 4 遍历审计：具名插槽填充内容（仅组件调用元素非空）同样要在分发前递归展开，
        // 否则具名内容里嵌套的组件调用 / PropsRef 不会被展开，客户端拿到未知 kind 渲染空白。
        val expandedNamedSlotContent = if (element.namedSlotContent.isEmpty()) {
            element.namedSlotContent
        } else {
            element.namedSlotContent.mapValues { (_, content) -> content.map { expandElement(it, depth) } }
        }
        val expandedMenus = element.menuBlocks.map { menu ->
            menu.copy(root = expandElement(menu.root, depth))
        }
        // RFC-RECOMPOSITION：conditionalContent 内的 branch.body 与 elseBranch 同样需要递归 expand
        val expandedConditional = element.conditionalContent?.let { content ->
            content.copy(
                branches = content.branches.map { it.copy(body = expandElement(it.body, depth)) },
                elseBranch = content.elseBranch?.let { expandElement(it, depth) },
            )
        }
        // $for：循环体内的组件调用同样需要递归 expand（iterable 表达式无需展开）
        val expandedFor = element.forContent?.let { content ->
            content.copy(body = expandElement(content.body, depth))
        }
        // tooltip / toast 的 Inline 形态内嵌 ElementAst 子树，同样需要递归展开组件调用；
        // 否则子树中引用的自定义组件不会被展开，客户端拿到未知 kind 导致渲染空白。
        val expandedTooltip = element.tooltipContent?.let { expandTooltipContent(it, depth) }
        val expandedToast = element.toastContent?.let { expandTooltipContent(it, depth) }
        val current = element.copy(
            children = expandedChildren,
            namedSlotContent = expandedNamedSlotContent,
            menuBlocks = expandedMenus,
            conditionalContent = expandedConditional,
            forContent = expandedFor,
            tooltipContent = expandedTooltip,
            toastContent = expandedToast,
        )

        // 判定是否为组件调用：M6-d 字符串 kind 调用，或 kind 首字母大写
        val isCall = current.isStringKindCall ||
            (current.kind.isNotEmpty() && current.kind[0].isUpperCase())
        if (!isCall) return current

        return expandComponentCall(current, depth)
    }

    /** 展开一次组件调用。 */
    private fun expandComponentCall(call: ElementAst, depth: Int): ElementAst {
        // DSL_E303：递归深度检查
        if (depth > MAX_DEPTH) {
            diag.error(
                DslError.E303_RECURSION_DEPTH_EXCEEDED,
                call.position,
                "组件展开递归深度超过 $MAX_DEPTH，可能存在循环引用",
            )
            return call
        }
        // 组件解析（ADR-0038）：按裸名定位——唯一即展开、歧义报 E326、未注册走白名单/E300。
        val rawComp = resolveComponent(call) ?: return call

        // 命名空间化：把组件自有 animation id 与组件内对它们的引用统一前缀化
        val comp = prefixedComponentCache.getOrPut(rawComp.id) {
            ComponentAnimationNamespacer.apply(rawComp)
        }
        // 记录被实际展开的组件，供 mergeComponentAnimations 按需合并
        expandedComponentIds.add(comp.id)

        val propMap = propValidator.buildPropMap(call, comp, dataDecls, UNIVERSAL_ELEMENT_PROPS) ?: return call

        // 校验组件 root 中的 PropsRef 引用合法性（DSL_E306）
        propValidator.validatePropsRefs(comp.root, comp)

        // clone root + props 替换
        val substituted = ExpressionSubstitutor.substituteElement(comp.root, propMap)
        // 透传通用元素属性（visible 等）：调用方未声明在 props 中、但属于
        // UIElement 通用属性时，直接挂到组件展开后的根元素上，避免要求每个
        // 自定义组件都重复声明同样的元素属性。
        val withUniversal = applyUniversalElementProps(substituted, call)
        // 计划 04 里程碑 5：组件级 state 提升 + 实例 id 改名。
        // 关键（R3）：state mangle 用的 instanceId 必须与 prefixIds 同源（都用 call.id），
        // 否则嵌套实例的 state 与元素 id 对不齐。改名只作用于组件模板自身（withUniversal），
        // 必须在 replaceChildrenSlots 注入调用方内容之前完成——调用方填充内容属调用方作用域，
        // 不能被本组件实例 id 改名。
        val withState = hoistComponentState(withUniversal, comp, call.id)
        // DSL_E324：调用方填充了组件 root 内未声明的具名插槽（拼写错误早暴露）。
        val declaredSlotNames = collectDeclaredSlotNames(comp.root)
        for (slotName in call.namedSlotContent.keys) {
            if (slotName !in declaredSlotNames) {
                diag.error(DslError.E324_FILL_UNDECLARED_SLOT, call.position, comp.id, slotName)
            }
        }
        // children 替换：匿名内容走 call.children，具名内容按名分发到对应命名洞。
        val withChildren = replaceChildrenSlots(withState, call.children, call.namedSlotContent)
        // id 前缀化
        val prefixed = prefixIds(withChildren, call.id)
        // 递归展开替换后的产物（组件 root 内可能嵌套其它组件）。
        // ADR-0038：组件 root 内的组件调用按「该组件文件」的 import 解析——切到 rawComp 的别名表，
        // 递归后还原调用方上下文；注入的插槽内容已在调用方上下文展开，不受影响。
        val savedImports = currentImports
        currentImports = toAliasMap(rawComp.imports)
        val result = expandElement(prefixed, depth + 1)
        currentImports = savedImports
        return result
    }

    /**
     * 组件解析（ADR-0038）：按裸名（末段）在注册表定位组件。
     * - 唯一候选 → 返回该组件。
     * - 多个候选 → 报 E326 歧义（提示用 import 别名消歧），返回 null。
     * - 0 候选 → 命中 IDE 跨文件白名单则静默返回 null（不展开不报错），否则报 E300。
     */
    private fun resolveComponent(call: ElementAst): ComponentAst? {
        val kind = call.kind
        // ADR-0038：先查本文件 import 别名表——命中即按其注册路径精确解析。
        currentImports[kind]?.let { path -> return registry.find(path) }
        val candidateIds = registry.findIdsByBareName(kind)
        return when {
            candidateIds.size == 1 -> registry.find(candidateIds[0])
            candidateIds.size >= 2 -> {
                diag.error(
                    DslError.E326_AMBIGUOUS_COMPONENT_NAME,
                    call.position,
                    kind,
                    candidateIds.joinToString(", "),
                )
                null
            }
            kind in knownComponentNames -> null
            else -> {
                diag.error(DslError.E300_COMPONENT_UNREGISTERED, call.position, kind)
                null
            }
        }
    }

    /**
     * 把 isChildrenSlot 节点替换为调用方提供的内容：
     * - 匿名洞（slotName == null）取 [anonymous]（调用方裸子元素）。
     * - 具名洞（slotName != null）取 [named] 中对应名下的内容；未填充则替换为空（与匿名洞「未提供则为空」一致）。
     */
    private fun replaceChildrenSlots(
        element: ElementAst,
        anonymous: List<ElementAst>,
        named: Map<String, List<ElementAst>>,
    ): ElementAst {
        val newChildren = mutableListOf<ElementAst>()
        for (child in element.children) {
            if (child.isChildrenSlot) {
                val slotName = child.slotName
                if (slotName == null) {
                    newChildren.addAll(anonymous)
                } else {
                    newChildren.addAll(named[slotName] ?: emptyList())
                }
            } else {
                newChildren.add(replaceChildrenSlots(child, anonymous, named))
            }
        }
        return element.copy(children = newChildren)
    }

    /** 收集组件 root 内声明的全部具名插槽名（用于 DSL_E324 校验调用方填充是否合法）。 */
    private fun collectDeclaredSlotNames(root: ElementAst): Set<String> {
        val names = linkedSetOf<String>()
        fun walk(e: ElementAst) {
            if (e.isChildrenSlot) {
                e.slotName?.let { names.add(it) }
                return
            }
            for (child in e.children) walk(child)
        }
        walk(root)
        return names
    }

    /**
     * 把调用方写在自定义组件根上的「通用元素属性」（visible / opacity / pointerEvents）
     * 挂到组件展开后的根元素上。调用方覆写优先于组件根原有同名属性。
     */
    private fun applyUniversalElementProps(expanded: ElementAst, call: ElementAst): ElementAst {
        val universalAssigns = call.props.filter { it.name in UNIVERSAL_ELEMENT_PROPS }
        if (universalAssigns.isEmpty() && call.modifiers.isEmpty()) return expanded
        val existing = expanded.props.toMutableList()
        for (pa in universalAssigns) {
            val idx = existing.indexOfFirst { it.name == pa.name }
            if (idx >= 0) existing[idx] = pa else existing.add(pa)
        }
        return expanded.copy(
            props = existing,
            modifiers = expanded.modifiers + call.modifiers,
        )
    }

    /** 给展开后的元素树中所有 id 加上 `instanceId/` 前缀。 */
    private fun prefixIds(element: ElementAst, instanceId: String): ElementAst {
        if (instanceId.isEmpty()) return element
        val newId = "$instanceId/${element.id}"
        val newChildren = element.children.map { prefixIds(it, instanceId) }
        val newMenus = element.menuBlocks.map { menu ->
            menu.copy(root = prefixIds(menu.root, instanceId))
        }
        return element.copy(id = newId, children = newChildren, menuBlocks = newMenus)
    }

    /**
     * 递归展开 [TooltipContentAst] 内嵌的元素子树。
     *
     * - [TooltipContentAst.Inline]：root 可能包含组件调用，需递归 expandElement
     * - [TooltipContentAst.Component]：保持原样（组件引用由 validateOverlayComponentRefs 校验）
     * - [TooltipContentAst.Text]：纯表达式，无子元素树，保持原样
     */
    private fun expandTooltipContent(content: TooltipContentAst, depth: Int): TooltipContentAst {
        return when (content) {
            is TooltipContentAst.Inline -> {
                // ADR-0036：tooltip 子树内复用的组件须为纯 props，先 fail-fast 拦截带 state 的组件。
                checkOverlayComponentNoState(content.root)
                content.copy(root = expandElement(content.root, depth))
            }
            is TooltipContentAst.Component -> content
            is TooltipContentAst.Text -> content
        }
    }

    /**
     * ADR-0036：overlay（tooltip）子树内禁止引用带内部 state 的组件。
     *
     * <p>组件 state 会提升进 screen.state，而 overlay 子树挂载后一次性快照、不订阅 state，会导致
     * 静默不刷新，且与 E730 设计意图冲突。故在展开前 fail-fast。只检查 tooltip 子树直接引用的组件。</p>
     */
    private fun checkOverlayComponentNoState(element: ElementAst) {
        val comp = registry.find(element.kind)
        if (comp?.state?.entries?.isNotEmpty() == true) {
            diag.error(DslError.E788_STATEFUL_COMPONENT_IN_OVERLAY, element.position, element.kind)
        }
        element.children.forEach(::checkOverlayComponentNoState)
        element.namedSlotContent.values.forEach { content -> content.forEach(::checkOverlayComponentNoState) }
        element.conditionalContent?.let { c ->
            c.branches.forEach { checkOverlayComponentNoState(it.body) }
            c.elseBranch?.let(::checkOverlayComponentNoState)
        }
        element.forContent?.let { checkOverlayComponentNoState(it.body) }
    }
}
